import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '../../../../../components/ui/label'
import { Link, useNavigate } from 'react-router-dom'
import { Combobox } from '../../../../../components/ui/combobox'
import { Button } from '../../../../../components/ui/button'
import { Input } from '../../../../../components/ui/input'

import { useSelector } from 'react-redux'
import { RootState } from '../../../../../redux'
import axios from '../../../../../lib/axios'
import toast from 'react-hot-toast'
import { useMutation, useQuery } from '@tanstack/react-query'
import useAuth from '../../../../../hooks/useAuth'
import { MultiSelect, Option } from '../../../../../components/ui/multi-select'
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group'
import { z } from 'zod'

const AddKassaPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { auth } = useAuth()

    const partners = useSelector((state:RootState)=>state.partners);

    const { data:deviescurrencies } = useQuery<DeviesCurrency[] | null>({
        queryKey: ['devies-currency'],
        queryFn: async () => {
            try {
                const { data } = await axios.get('/deviescurrency/get', { params: {
                    token: auth.data?.token
                }})
                if(data.errorcode!==0){ throw new Error(data.message) }
                return data.object
            } catch (error) {
                console.log(error)
                toast.error(t('settings.fc.toast_error_fetch_foreign'), { id:'devies-currency' })
                return null
            }
        },
        refetchOnWindowFocus: false
    })

    const { mutate, isLoading } = useMutation({
        mutationFn: async () => {
            try {
                toast.loading(t('settings.fc.toast_loading_add_kassa'), { id:'kassas' })
                const { data } = await axios.post('/kassa/create', {
                    token: auth.data?.token,
                    relnr: selected_relnr,
                    cn: cn,
                    vault: vault,
                    omschrijving: description,
                    deviescurrencyid: selected_valuta?.map(v=>v.value).join(';')
                })
                if(data.errorcode!==0){ throw new Error(data.message) }
                toast.success(t('settings.fc.toast_success_add_kassa'), { id:'kassas' })
                clearStates()
                navigate(-1)
            } catch (error) {
                toast.error(t('settings.fc.toast_error_add_kassa'), { id:'kassas' })
            }
        }
    })
    
    const [selected_relnr, setselected_relnr] = useState<string | undefined>(undefined);
    const [selected_valuta, setselected_valuta] = useState<Option[] | undefined>(undefined);
    const [description, setdescription] = useState('');
    const [vault, setvault] = useState('');
    const [cn, setcn] = useState('c');

    function clearStates() {
        setselected_relnr(undefined)
        setselected_valuta(undefined)
        setdescription('')
        setvault('')
        setcn('c')
    }

    function isComplete() {
        try {
            const s = z.object({
                relnr: z.string().min(1),
                valuta: z.string().min(1),
                description: z.string().min(1),
                vault: z.string().min(1),
                cn: z.enum(['c', 'n'])
            })

            s.parse({
                relnr: selected_relnr,
                valuta: selected_valuta?.map(v=>v.value).join(';'),
                description: description,
                vault: vault,
                cn: cn
            })
            
            return true
        } catch (error) {
            return false
        }
    }

    let partnersArr = Array.isArray(partners) ? (partners.map((item) => ({value:item.relnr, label:item.name}))) : []
    const deviescurrenyOptions = Array.isArray(deviescurrencies) ? deviescurrencies.map(dc=>({value:dc.deviescurrencyid.toString(), label:dc.deviescurrencyname})) : []

  return (
    <div className="w-full p-4 overflow-auto max-h-[100dvh] min-h-[100dvh] h-full md:p-8">
        <div className='flex items-center justify-between'>
            <h1 className='text-2xl font-semibold'>{t('settings.fc.add_kassa_title')}</h1>
            <Link to=".."><Button variant='secondary'>{t('settings.fc.back')}</Button></Link>
        </div>

        <div className='grid grid-cols-2 gap-4 p-4 mt-4 border rounded-sm md:mt-8'>
            <div>
                <Label>{t('settings.fc.partner')}</Label>
                <Combobox selectedValue={selected_relnr} onValueChange={({value})=>setselected_relnr(value.toString())} options={partnersArr} placeholder={t('settings.fc.partner_placeholder')||''} input_placeholder={t('settings.fc.partner_search')||''}/>
            </div>
            <div>
                <Label>{t('settings.fc.description')}</Label>
                <Input value={description} onChange={(e)=>setdescription(e.target.value)} />
            </div>
            <div className='flex items-end gap-8 col-span-full'>
                
                <div>
                    <Label>Vault</Label>
                    <Input value={vault} onChange={(e)=>setvault(e.target.value)} maxLength={3} className='w-[100px]' />
                </div> 
                <RadioGroup value={cn} onValueChange={(v)=>setcn(v)}>
                    <div className='flex items-center gap-1'>
                        <RadioGroupItem value='c' id='c'/>
                        <Label htmlFor='c'>{t('settings.fc.coins')}</Label>
                    </div>
                    
                    <div className='flex items-center gap-1'>
                        <RadioGroupItem value='n' id='n'/>
                        <Label htmlFor='n'>{t('settings.fc.notes')}</Label>
                    </div>
                </RadioGroup>
            </div>
             
            <div>
                <Label>{t('settings.fc.active_valutas')}</Label>
                <MultiSelect selectedValue={selected_valuta} onValueChange={(v)=>{setselected_valuta(v)}} options={deviescurrenyOptions} placeholder={t('settings.fc.valuta_placeholder')||''}/>
            </div>

            <div className='col-span-full'>
                <Button onClick={()=>{mutate()}} disabled={!isComplete() || isLoading} className='gap-2'>
                    {t('settings.fc.save')}
                </Button>
            </div>
        </div>
    </div>
  )
}

export default AddKassaPage